import { getCurrencyCode } from "@/features/branding";
import { downloadXlsxFile } from "@/features/custom_downloads";
import { useGetPortfolioSummaryResultsQuery } from "@/store/services/facades/endpoints/pea";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { useGetAdaptationsQuery } from "@/store/services/supabase";
import { useSummaryActions } from "@/tools/aggregate/portfolio-export/hooks/useSummaryActions";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import {
  downloadPortfolioSummary,
  enqueuePortfolioAdaptationSummary,
} from "@/tools/aggregate/portfolio-export/utils/stream";
import { buildAggregateSummaryXlsxFilename } from "@/tools/aggregate/portfolio-export/utils/string";
import { AdaptationType } from "@/types/adaptation";
import * as Sentry from "@sentry/nextjs";
import { notification } from "antd";
import { useCallback, useState } from "react";

export type SummaryStatus =
  | { kind: "missing"; generate: () => Promise<void>; isGenerating: boolean }
  | { kind: "empty"; regenerate: () => Promise<void>; isRegenerating: boolean } // when summary report is stale/missing in results db but summary_id exists in hub
  | { kind: "generating" }
  | { kind: "loading" }
  | { kind: "error"; error: unknown }
  | { kind: "ready"; data: PeaSummaryResponse };

export const useBaselineSummaryStatus = (schema: PortfolioExportJobSchema, enabled: boolean): SummaryStatus => {
  const summary = useSummaryActions();

  const hasSummaryId = !!schema.summaryId;
  const isGenerating = hasSummaryId && !schema.isSummaryGenerated;
  const isReadyToFetch = hasSummaryId && !!schema.isSummaryGenerated;

  const { data, isLoading, isFetching, isSuccess, isError, error } = useGetPortfolioSummaryResultsQuery(
    { summaryId: schema.summaryId },
    { skip: !enabled || !isReadyToFetch }
  );

  const isEmptyResult = isReadyToFetch && isSuccess && !isFetching && Array.isArray(data) && data.length === 0;

  if (!enabled) return { kind: "loading" };
  if (!hasSummaryId) {
    return {
      kind: "missing",
      generate: () => summary.generate(schema),
      isGenerating: summary.isGenerating,
    };
  }
  if (isGenerating) return { kind: "generating" };
  if (isLoading || isFetching) return { kind: "loading" };
  if (isError) return { kind: "error", error };
  if (isSuccess && Array.isArray(data) && data.length > 0) {
    return { kind: "ready", data };
  }
  if (isEmptyResult) {
    return {
      kind: "empty",
      regenerate: () => summary.regenerate(schema),
      isRegenerating: summary.isRegenerating,
    };
  }
  return { kind: "loading" };
};

export type AdaptedSummaryStatus = {
  status: SummaryStatus;
  download: () => Promise<void>;
  isDownloading: boolean;
};

export const useAdaptedSummaryStatus = (schema: PortfolioExportJobSchema, enabled: boolean): AdaptedSummaryStatus => {
  const [isGeneratingAdaptation, setIsGeneratingAdaptation] = useState(false);
  const [isRegeneratingAdaptation, setIsRegeneratingAdaptation] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    data: adaptedAdaptations,
    isLoading: adaptationsIsLoading,
    isError: adaptationsIsError,
    error: adaptationsError,
  } = useGetAdaptationsQuery({ portfolioId: schema.id, adaptationTypes: ["enhanced_resilience"] }, { skip: !enabled });

  const adaptedData = adaptedAdaptations?.[0];
  const hasSummaryId = !!adaptedData?.summary_id;
  const isAdaptationGenerating = hasSummaryId && !adaptedData?.is_summary_generated;
  const isReadyToFetch = hasSummaryId && !!adaptedData?.is_summary_generated;

  const { data, isLoading, isFetching, isSuccess, isError, error } = useGetPortfolioSummaryResultsQuery(
    { summaryId: adaptedData?.summary_id ?? "" },
    { skip: !enabled || !isReadyToFetch }
  );

  const generate = useCallback(async () => {
    if (!adaptedData?.id) return;
    setIsGeneratingAdaptation(true);
    try {
      await enqueuePortfolioAdaptationSummary(
        schema.id,
        adaptedData.summary_id ?? "",
        schema.secret,
        adaptedData.stamps ?? [],
        adaptedData.id,
        schema.regionType
      );
    } finally {
      setIsGeneratingAdaptation(false);
    }
  }, [adaptedData?.id, adaptedData?.summary_id, adaptedData?.stamps, schema.id, schema.secret, schema.regionType]);

  const regenerate = useCallback(async () => {
    if (!adaptedData?.id) return;
    setIsRegeneratingAdaptation(true);
    try {
      // force summary regen by passing empty previous summary id
      await enqueuePortfolioAdaptationSummary(
        schema.id,
        "",
        schema.secret,
        adaptedData.stamps ?? [],
        adaptedData.id,
        schema.regionType
      );
    } finally {
      setIsRegeneratingAdaptation(false);
    }
  }, [adaptedData?.id, adaptedData?.stamps, schema.id, schema.secret, schema.regionType]);

  const download = useCallback(async () => {
    if (!adaptedData?.summary_id) return;
    setIsDownloading(true);
    const currencyCode = getCurrencyCode();
    const resiliencePathway = "enhanced_resilience" as AdaptationType;
    const settings = {
      ...(currencyCode ? { currency_code: currencyCode } : undefined),
      resilience_pathway: resiliencePathway,
    };
    try {
      const buffer = await downloadPortfolioSummary(adaptedData.summary_id, settings);
      if (buffer) {
        downloadXlsxFile(buffer, {
          filename: buildAggregateSummaryXlsxFilename({
            schemaName: schema.name,
            isLargeSite: schema.isLargeSite,
            adaptationType: resiliencePathway,
          }),
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      notification.error({
        duration: 0,
        message: "Summary Failed",
        description: `There was a problem downloading "${schema.name}". Please try again. If the issue persists, contact support.\n${message}`,
      });

      Sentry.captureException(`Auto Resilience Summary Download Error: ${err}`, {
        tags: { section: "Summary Download" },
        extra: {
          userId: schema.userId,
          portfolioId: schema.id,
          groupId: schema.groupId,
          summaryId: adaptedData.summary_id,
          adaptationId: adaptedData.id,
          resiliencePathway,
        },
      });
    } finally {
      setIsDownloading(false);
    }
  }, [
    adaptedData?.summary_id,
    adaptedData?.id,
    schema.name,
    schema.isLargeSite,
    schema.id,
    schema.userId,
    schema.groupId,
  ]);

  //adaptation summary_id exists but summary results are empty (stale)
  const isEmptyResult = isReadyToFetch && isSuccess && !isFetching && Array.isArray(data) && data.length === 0;

  const status: SummaryStatus = (() => {
    if (!enabled) return { kind: "loading" };
    if (adaptationsIsLoading) return { kind: "loading" };
    if (adaptationsIsError) return { kind: "error", error: adaptationsError };
    if (!adaptedData) {
      return { kind: "ready", data: [] };
    }
    if (!hasSummaryId) {
      return { kind: "missing", generate, isGenerating: isGeneratingAdaptation };
    }
    if (isAdaptationGenerating) return { kind: "generating" };
    if (isLoading || isFetching) return { kind: "loading" };
    if (isError) return { kind: "error", error: error ?? "Empty summary response" };
    if (isSuccess && Array.isArray(data) && data.length > 0) {
      return { kind: "ready", data };
    }
    if (isEmptyResult) {
      return { kind: "empty", regenerate, isRegenerating: isRegeneratingAdaptation };
    }
    return { kind: "loading" };
  })();

  return { status, download, isDownloading };
};
